<script setup lang="ts">
import { debounce } from 'lodash'
import qs from 'qs'
import { pricesApi } from '@/api/client'
import { Ref, ref, onBeforeUnmount, watch } from 'vue'
import { useUserStore } from '@/stores/UserStore'
import { CatalogFilters, Category, Price, PricesStats } from '@/types'
import Flag from './Flag.vue'
import Grade from './Grade.vue'
import Battery from './Battery.vue'
import BrandLogo from './BrandLogo.vue'
import Broadcaster from '@/pusher'
import { Channel } from 'pusher-js'
import DropdownFilter from './DropdownFilter.vue'
import PriceSideRow from './PriceSideRow.vue'
import LoadMoreFooter from './LoadMoreFooter.vue'
import SortDropdown from './SortDropdown.vue'
import FilterPanel from './FilterPanel.vue'
import { useI18n } from 'vue-i18n'
import { useFilterState } from '@/composables/useFilterState'
import { usePagination } from '@/composables/usePagination'
import { Skeleton } from '@/components/ui/skeleton'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { Download, Loader2 } from 'lucide-vue-next'

const { t } = useI18n()

const category: Ref<Category | null> = ref(null)
const list: Ref<Array<Price>> = ref([])
const stats: Ref<PricesStats | null> = ref(null)
const itemsCount: Ref<number> = ref(0)
const filters: Ref<CatalogFilters | null> = ref(null)
const loading = ref(true)
const loadingMore = ref(false)

const user = useUserStore()

const {
    activeBrandsFilters,
    activeModelsFilters,
    activeGradesFilters,
    activeSearchFilter,
    selectedCriteria,
    filtersCount,
    hasActiveFilters,
    hasData,
    resetFilters: resetFilterState,
} = useFilterState({ items: list, defaultSort: 'grade:desc' })

const { page, nextPage, resetPage } = usePagination()

const processingPrices: Ref<boolean> = ref(false)
const processingItems: Ref<boolean> = ref(false)

let notificationChannel: Channel | null = null

const props = defineProps<{
    id: number
}>()

const loadCategoryInformation = (id: number) => {
    if (user.profile?.categories === undefined) {
        return
    }

    category.value = user.profile.categories.find((element) => Number(element.id) === Number(id)) ?? null
}

const loadMorePrices = async (id: number) => {
    try {
        loadingMore.value = true
        nextPage()

        const queryString = qs.stringify(getQuery())
        const pricesResponse = await pricesApi.list(id, queryString)

        list.value = list.value.concat(pricesResponse.data.items)
    } catch (error) {
        console.error(error)
    } finally {
        loadingMore.value = false
    }
}

const sortingCriteria = [
    { label: 'Highest grade', value: 'grade:desc' },
    { label: 'Lowest grade', value: 'grade:asc' },
]

const getQuery = () => {
    const query: {
        models?: string[]
        products?: string[]
        page?: number
        brands?: string[]
        grades?: string[]
        search?: string
        sort?: string
    } = {}

    if (activeModelsFilters.value.length) {
        query.models = activeModelsFilters.value
    }

    if (activeBrandsFilters.value.length) {
        query.brands = activeBrandsFilters.value
    }

    if (activeGradesFilters.value.length) {
        query.grades = activeGradesFilters.value
    }

    if (activeSearchFilter.value) {
        query.search = activeSearchFilter.value
    }

    query.page = page.value
    query.sort = selectedCriteria.value

    return query
}

const loadPrices = async (id: number) => {
    try {
        loading.value = true

        const queryString = qs.stringify(getQuery())
        const pricesResponse = await pricesApi.list(id, queryString)

        itemsCount.value = pricesResponse.data.items_count
        stats.value = pricesResponse.data.stats
        list.value = pricesResponse.data.items
        filters.value = pricesResponse.data.filters
    } catch (error) {
        console.error(error)
    } finally {
        loading.value = false
    }
}

const loadMore = async () => {
    await loadMorePrices(props.id)
}

const onModelsFilterChange = async (_event: Event) => {
    resetPage()
    await loadPrices(props.id)
}

const onProductsFilterChange = async () => {
    resetPage()
    await loadPrices(props.id)
}

const onBrandsFilterChange = async (_event: Event) => {
    activeModelsFilters.value = []
    resetPage()
    await loadPrices(props.id)
}

const onGradesFilterChange = async (_event: Event) => {
    resetPage()
    await loadPrices(props.id)
}

const onSortingChange = async () => {
    resetPage()
    await loadPrices(props.id)
}

const onSearchInputChange = debounce(onProductsFilterChange, 500)

// The filters follow the URL on their own (see useFilterState): a sidebar link
// carries none, a back navigation carries the ones it left with
watch(
    () => props.id,
    async (id) => {
        resetPage()
        loadCategoryInformation(Number(id))
        await loadPrices(Number(id))
    },
)

const resetFilters = () => {
    resetFilterState()
    resetPage()
}

const onResetFiltersBtnClick = async () => {
    resetFilters()
    await loadPrices(props.id)
}

const getExportFilters = () => {
    const query: {
        models?: string[]
        products?: string[]
        brands?: string[]
        grades?: string[]
        search?: string
    } = {}

    if (activeModelsFilters.value.length) {
        query.models = activeModelsFilters.value
    }

    if (activeBrandsFilters.value.length) {
        query.brands = activeBrandsFilters.value
    }

    if (activeGradesFilters.value.length) {
        query.grades = activeGradesFilters.value
    }

    if (activeSearchFilter.value) {
        query.search = activeSearchFilter.value
    }

    return query
}

const unsubscribe = () => {
    console.log('Unsubscribing...')
    Broadcaster.get().connector.unsubscribe(`private-user.${user.profile?.id}.notifications`)
    notificationChannel?.unbind('prices-export-completed')
    notificationChannel?.unbind('items-export-completed')
    notificationChannel = null
}

const subscribe = () => {
    if (notificationChannel !== null) {
        return
    }

    console.log('Subscribing...')
    notificationChannel = Broadcaster.get().connector.subscribe(`private-user.${user.profile?.id}.notifications`)
}

const download = (data: { filename: string }) => {
    console.log('Downloading..', data.filename)
    window.location.href = `/api/download/${data.filename}`
}

const onPricesExportBtnClick = async () => {
    try {
        processingPrices.value = true

        notificationChannel?.bind('prices-export-completed', (data: { filename: string }) => {
            console.log('Event received', data)
            processingPrices.value = false
            download(data)
        })

        await pricesApi.exportPrices(props.id, getExportFilters())
    } catch {
        // unsubscribe();
    }
}

const onItemsExportBtnClick = async () => {
    try {
        processingItems.value = true

        notificationChannel?.bind('items-export-completed', (data: { filename: string }) => {
            console.log('Event received', data)
            processingItems.value = false
            download(data)
        })

        await pricesApi.exportItems(props.id, getExportFilters())
    } catch {
        // unsubscribe();
    }
}

onBeforeUnmount(() => unsubscribe())

subscribe()
loadCategoryInformation(props.id)
loadPrices(props.id)
</script>

<template>
    <div class="space-y-4">
        <div class="-mx-6 sm:-mx-8 border-b px-6 sm:px-8 pb-4">
            <h1 class="text-3xl font-bold tracking-tight">
                {{ t('prices.title', { category: category?.name }) }}
            </h1>
        </div>
    </div>

    <div class="mt-8">
        <!-- Filters & Stats -->
        <div class="flex flex-wrap items-center justify-between gap-4 mb-4">
            <div v-if="hasData">
                <div class="text-sm font-medium">
                    {{ t('prices.models') }}: <span class="font-bold">{{ stats?.products }}</span>
                    <span class="mx-2 text-muted-foreground">·</span>
                    {{ t('prices.monitoredDeals') }}: <span class="font-bold">{{ stats?.monitored_items }}</span>
                </div>
                <div v-if="stats?.last_update" class="text-xs text-muted-foreground">
                    {{ t('common.updatedAt', { updatedAt: stats.last_update }) }}
                </div>
            </div>

            <div class="flex flex-row gap-2 ml-auto">
                <template v-if="hasData">
                    <Input
                        v-model="activeSearchFilter"
                        type="text"
                        class="h-10 w-48"
                        :placeholder="t('common.filters.search')"
                        @update:model-value="onSearchInputChange"
                    />

                    <FilterPanel
                        :filters-count="filtersCount"
                        :show="true"
                        @reset="onResetFiltersBtnClick"
                    >
                        <DropdownFilter
                            v-model:value="activeBrandsFilters"
                            :options="filters?.brands || []"
                            :show="!!filters?.brands.length"
                            :button-label="t('common.filters.byBrand')"
                            options-label="Brands"
                            @update:value="onBrandsFilterChange"
                        />

                        <DropdownFilter
                            v-model:value="activeModelsFilters"
                            :options="filters?.models || []"
                            :show="!!filters?.models.length"
                            :button-label="t('common.filters.byModels')"
                            :options-label="t('common.models')"
                            @update:value="onModelsFilterChange"
                        />

                        <DropdownFilter
                            v-model:value="activeGradesFilters"
                            :options="filters?.grades || []"
                            :show="!!filters?.grades.length"
                            :button-label="t('common.filters.byGrade')"
                            :options-label="t('common.grades')"
                            @update:value="onGradesFilterChange"
                        />
                    </FilterPanel>

                    <SortDropdown
                        v-model="selectedCriteria"
                        :criteria="sortingCriteria"
                        :show="true"
                        @change="onSortingChange"
                    />

                    <Button
                        variant="secondary"
                        :disabled="processingPrices || processingItems"
                        @click.prevent="onPricesExportBtnClick"
                    >
                        {{ t('common.exportToCsv') }}
                        <Loader2 v-if="processingPrices" class="size-4 ml-2 animate-spin" />
                        <Download v-else class="size-4 ml-2" />
                    </Button>

                    <Button
                        v-if="user.configuration?.export_items_enabled"
                        variant="secondary"
                        :disabled="processingPrices || processingItems"
                        @click.prevent="onItemsExportBtnClick"
                    >
                        {{ t('common.exportToCsv') }} Items
                        <Loader2 v-if="processingItems" class="size-4 ml-2 animate-spin" />
                        <Download v-else class="size-4 ml-2" />
                    </Button>
                </template>
            </div>
        </div>

        <!-- Table -->
        <div class="overflow-x-auto">
            <!-- Header -->
            <div v-if="!loading && list.length > 0" class="prices-row border-b py-3 text-xs font-medium text-muted-foreground uppercase">
                <div class="prices-col-brand"></div>
                <div class="prices-col-product">{{ t('prices.table.headings.product') }}</div>
                <div class="prices-col-prices">
                    <div class="flex items-center">
                        <div v-if="user.isMultiCountry" class="w-6 shrink-0 mr-4"></div>
                        <div class="w-24 shrink-0 pr-4"></div>
                        <div class="w-24 shrink-0 text-left">Min</div>
                        <div class="w-24 shrink-0 text-left">Max</div>
                        <div class="w-24 shrink-0 text-left">Avg</div>
                        <div class="w-32 shrink-0 text-left">30d / 60d</div>
                        <div class="w-20 shrink-0 pl-3 text-left">Listings</div>
                        <div class="w-40 shrink-0 text-left">Top competitor</div>
                    </div>
                </div>
            </div>

            <!-- Skeleton rows -->
            <template v-if="loading">
                <div
                    v-for="n in 8"
                    :key="`skeleton-${n}`"
                    class="prices-row border-b py-3"
                >
                    <div class="prices-col-brand">
                        <Skeleton class="size-4 rounded" />
                    </div>
                    <div class="prices-col-product space-y-2">
                        <Skeleton class="h-4 w-3/4" />
                        <Skeleton class="h-3 w-1/2" />
                    </div>
                    <div class="prices-col-prices">
                        <div class="flex items-center gap-4">
                            <Skeleton class="h-4 w-20" />
                            <Skeleton class="h-4 w-16" />
                            <Skeleton class="h-4 w-16" />
                            <Skeleton class="h-4 w-16" />
                            <Skeleton class="h-4 w-24" />
                            <Skeleton class="h-4 w-12" />
                            <Skeleton class="h-4 w-32" />
                        </div>
                    </div>
                </div>
            </template>

            <!-- Empty state: no data at all -->
            <div v-if="!loading && list.length === 0 && !hasActiveFilters" class="py-12 text-center text-sm text-muted-foreground">
                {{ t('prices.empty') }}
            </div>

            <!-- No results: filters active but nothing matches -->
            <div v-else-if="!loading && list.length === 0" class="py-12 text-center text-sm text-muted-foreground">
                {{ t('common.noResults') }}
            </div>

            <!-- Rows -->
            <div
                v-for="price in list"
                v-show="!loading"
                :key="price.name"
                class="prices-row border-b py-3 transition-colors hover:bg-muted/50"
            >
                <!-- Brand -->
                <div class="prices-col-brand">
                    <BrandLogo v-if="price.brand_name" :brand="price.brand_name" colored />
                </div>

                <!-- Product -->
                <div class="prices-col-product min-w-0">
                    <div class="flex items-center gap-2">
                        <span class="font-medium text-sm 2xl:text-base">{{ price.name }}</span>
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger as-child>
                                    <Badge :variant="price.online_items >= 20 ? 'warning' : 'secondary'" class="shrink-0">
                                        <span class="relative flex size-1.5 mr-1">
                                            <span class="absolute inline-flex h-full w-full animate-ping rounded-full bg-current opacity-50" />
                                            <span class="relative inline-flex size-1.5 rounded-full bg-current" />
                                        </span>
                                        {{ price.online_items }}
                                        <span class="hidden 2xl:inline ml-0.5">{{ t('catalog.details.competitorGrades.deals') }}</span>
                                    </Badge>
                                </TooltipTrigger>
                                <TooltipContent class="2xl:hidden">
                                    {{ price.online_items }} active listings from competitors
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    </div>
                    <div class="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                        <span v-if="price.storage">{{ price.storage }}</span>
                        <Battery :status="price.battery_type" />
                        <Grade :grade="price.grade" plain />
                    </div>
                </div>

                <!-- Prices -->
                <div class="prices-col-prices">
                    <div class="divide-y divide-border">
                        <div
                            v-for="countryPrice in price.prices"
                            :key="countryPrice.country"
                            class="flex items-center py-3"
                        >
                            <div v-if="user.isMultiCountry" class="w-6 shrink-0 mr-4 flex justify-center">
                                <Flag :country="countryPrice.country" :size="16" />
                            </div>

                            <div>
                                <PriceSideRow
                                    v-if="countryPrice.resell"
                                    :prices="countryPrice.resell"
                                    :currency-symbol="countryPrice.currency_symbol"
                                    side="resell"
                                />

                                <PriceSideRow
                                    v-if="countryPrice.buyback"
                                    :prices="countryPrice.buyback"
                                    :currency-symbol="countryPrice.currency_symbol"
                                    side="buyback"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Load more skeleton rows -->
            <template v-if="loadingMore">
                <div
                    v-for="n in 4"
                    :key="`skeleton-more-${n}`"
                    class="prices-row border-b py-3"
                >
                    <div class="prices-col-brand">
                        <Skeleton class="size-4 rounded" />
                    </div>
                    <div class="prices-col-product space-y-2">
                        <Skeleton class="h-4 w-3/4" />
                        <Skeleton class="h-3 w-1/2" />
                    </div>
                    <div class="prices-col-prices">
                        <div class="flex items-center gap-4">
                            <Skeleton class="h-4 w-20" />
                            <Skeleton class="h-4 w-16" />
                            <Skeleton class="h-4 w-16" />
                            <Skeleton class="h-4 w-16" />
                            <Skeleton class="h-4 w-24" />
                            <Skeleton class="h-4 w-12" />
                            <Skeleton class="h-4 w-32" />
                        </div>
                    </div>
                </div>
            </template>
        </div>

        <LoadMoreFooter v-if="list.length > 0" :current-count="list.length" :total-count="itemsCount" @load-more="loadMore" />
    </div>
</template>

<style scoped>
.prices-row {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding-left: 0.5rem;
    padding-right: 0.5rem;
}

.prices-col-brand {
    width: 1rem;
    flex-shrink: 0;
}

.prices-col-product {
    flex: 1 1 0%;
    min-width: 14rem;
    max-width: 48rem;
}

.prices-col-prices {
    flex-shrink: 0;
}

@media (min-width: 1536px) {
    .prices-row {
        gap: 1.5rem;
    }
}
</style>
